import { normalizeAssistantContent } from "./normalizeAssistantContent";

const MERMAID_PREFIXES = [
  "graph ",
  "flowchart ",
  "sequencediagram",
  "classdiagram",
  "statediagram",
  "statediagram-v2",
  "erdiagram",
  "journey",
  "mindmap",
  "timeline",
  "gantt",
  "gitgraph",
  "pie",
  "requirementdiagram",
  "c4context",
  "c4container",
  "c4component",
  "c4dynamic",
  "c4deployment",
];

function hasFence(text: string): boolean {
  return /```/.test(text);
}

function looksLikeMarkdownTable(text: string): boolean {
  if (!text.includes("|")) return false;
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  for (let i = 0; i < lines.length - 1; i += 1) {
    if (
      lines[i].startsWith("|") &&
      lines[i + 1].startsWith("|") &&
      lines[i + 1].includes("---")
    ) {
      return true;
    }
  }
  return false;
}

function looksLikeMermaid(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed.includes("```")) return false;
  const first = trimmed.split("\n").find((line) => line.trim())?.trim().toLowerCase() || "";
  return MERMAID_PREFIXES.some((prefix) => first.startsWith(prefix));
}

function parseJsonBlock(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (
    !(trimmed.startsWith("{") && trimmed.endsWith("}")) &&
    !(trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    return null;
  }
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return null;
  }
}

function looksLikeXml(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed.includes("```")) return false;
  if (!(trimmed.startsWith("<?xml") || trimmed.startsWith("<"))) return false;
  if (!trimmed.endsWith(">")) return false;
  return /<([A-Za-z_][\w:.-]*)(?:\s[^>]*)?>[\s\S]*<\/\1>/.test(trimmed);
}

function looksLikeDelimited(text: string, delimiter: "," | "\t"): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed.includes("```") || looksLikeMarkdownTable(trimmed)) return false;
  const lines = trimmed
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return false;
  const counts = lines.slice(0, 6).map((line) => line.split(delimiter).length - 1);
  if (counts.some((count) => count < 1)) return false;
  return counts.every((count) => count === counts[0]);
}

function looksLikeYaml(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed.includes("```")) return false;
  const lines = trimmed
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line) => line.trim());
  if (lines.length < 2) return false;
  let keyValueLines = 0;
  for (const line of lines) {
    const clean = line.trim();
    if (clean.startsWith("#") || clean.startsWith("## ")) return false;
    if (/^[A-Za-z0-9_.-]+\s*:\s*.+$/.test(clean) || /^-\s+[A-Za-z0-9_.-]+\s*:\s*.+$/.test(clean)) {
      keyValueLines += 1;
    } else {
      return false;
    }
  }
  return keyValueLines >= 2;
}

function detectRawCodeLanguage(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed || trimmed.includes("```")) return null;
  const lines = trimmed
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return null;
  const joined = lines.slice(0, 12).join("\n");

  if (
    /^(public|private|global|protected)?\s*(with sharing|without sharing)?\s*class\s+[A-Za-z_][A-Za-z0-9_]*\b/m.test(joined) ||
    /^trigger\s+[A-Za-z_][A-Za-z0-9_]*\s+on\s+[A-Za-z_][A-Za-z0-9_]*\b/m.test(joined) ||
    /^@isTest\b/m.test(joined)
  ) {
    return "apex";
  }

  if (/^SELECT\s+[\s\S]+?\s+FROM\s+[A-Za-z_][A-Za-z0-9_]*\b/i.test(joined)) {
    return "soql";
  }

  if (
    /^export\s+(default\s+)?(function|class|const)\b/m.test(joined) ||
    /^import\s+.+\s+from\s+["'][^"']+["'];?$/m.test(joined) ||
    /^function\s+[A-Za-z_][A-Za-z0-9_]*\s*\(/m.test(joined)
  ) {
    return /:\s*[A-Za-z_][A-Za-z0-9_<>\[\]\s|&?,]*/.test(joined) ? "typescript" : "javascript";
  }

  if (
    /^const\s+[A-Za-z_][A-Za-z0-9_]*\s*=\s*\(/m.test(joined) ||
    /^let\s+[A-Za-z_][A-Za-z0-9_]*\s*=\s*\(/m.test(joined) ||
    /^var\s+[A-Za-z_][A-Za-z0-9_]*\s*=\s*\(/m.test(joined)
  ) {
    return "javascript";
  }

  if (
    /^\.[A-Za-z_-][A-Za-z0-9_-]*\s*\{/m.test(joined) ||
    /^#[A-Za-z_-][A-Za-z0-9_-]*\s*\{/m.test(joined) ||
    /^[A-Za-z][A-Za-z0-9:_-]*(?:\s*,\s*[A-Za-z][A-Za-z0-9:_-]*)*\s*\{/m.test(joined) ||
    /^@media\b/m.test(joined) ||
    /^@supports\b/m.test(joined)
  ) {
    return "css";
  }

  if (/^#!/.test(trimmed) || /^(echo|export|if\s+\[|fi|for\s+\w+\s+in|curl\s+|npm\s+|pnpm\s+|yarn\s+)/m.test(joined)) {
    return "bash";
  }

  if (
    /^def\s+[A-Za-z_][A-Za-z0-9_]*\s*\(/m.test(joined) ||
    /^import\s+[A-Za-z0-9_.]+$/m.test(joined) ||
    /^from\s+[A-Za-z0-9_.]+\s+import\s+/m.test(joined)
  ) {
    return "python";
  }

  if (
    /^(public\s+)?class\s+[A-Z][A-Za-z0-9_]*\s*\{/m.test(joined) ||
    /^public\s+(static\s+)?(void|int|String|boolean|long|double|List<)/m.test(joined)
  ) {
    return "java";
  }

  if (/^package\s+[A-Za-z0-9_.]+\s*;$/m.test(joined) || /^using\s+[A-Za-z0-9_.]+\s*;$/m.test(joined)) {
    return "java";
  }

  if (
    /^<apex:[A-Za-z0-9:_-]+/m.test(joined) ||
    /^<[A-Za-z][\w:-]*(\s[^>]*)?>[\s\S]*<\/[A-Za-z][\w:-]*>$/m.test(trimmed)
  ) {
    return "xml";
  }

  return null;
}

function wrapFence(language: string, text: string): string {
  return `\`\`\`${language}\n${text.trim()}\n\`\`\``;
}

export function prepareRenderableAssistantContent(
  content: string,
  options?: { isStreaming?: boolean },
): string {
  const normalized = normalizeAssistantContent(content);
  if (!normalized) return normalized;
  if (hasFence(normalized) || looksLikeMarkdownTable(normalized)) return normalized;

  if (looksLikeMermaid(normalized)) {
    return wrapFence("mermaid", normalized);
  }

  const parsedJson = parseJsonBlock(normalized);
  if (parsedJson) {
    return wrapFence("json", parsedJson);
  }

  if (looksLikeXml(normalized)) {
    return wrapFence("xml", normalized);
  }

  if (looksLikeDelimited(normalized, "\t")) {
    return wrapFence("tsv", normalized);
  }

  if (looksLikeDelimited(normalized, ",")) {
    return wrapFence("csv", normalized);
  }

  if (looksLikeYaml(normalized)) {
    return wrapFence("yaml", normalized);
  }

  const rawCodeLanguage = detectRawCodeLanguage(normalized);
  if (rawCodeLanguage) {
    return wrapFence(rawCodeLanguage, normalized);
  }

  if (options?.isStreaming) return normalized;

  return normalized;
}
